// React and Material-UI imports
import React, { useContext, useEffect, useState } from "react";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";

// Material React Table imports
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";

// Local imports
import { useScrollPosition } from "../../hooks/useScrollPosition";
import PositionsTable from "../table/PositionsTable";
import TradesTable from "../table/TradesTable";
import { fetchConvertNukes } from "../../services/Axios";
import { PositionsDataContext } from "../../context/PositionsDataContext";
import { getConvertsPnlTableColumns } from "../table/columnsConfig/pnlTable";
import { getNukesTableColumns } from "../table/columnsConfig/nukesDataColumns";
import { CardWrapper, PageHeader, TabNavigation } from "../ui";
import {
  getCommonBodyCellProps,
  getCommonHeadCellProps,
} from "../table/columnsConfig/commonUtils";
import ExportToCSVButton from "../ExportToCsvButton";
import { formatCurrency, formatNumber } from "../../utils/dataCalculations";

const columnsToExport = [
  "Security_Name",
  "BB_Ticker_Code_Underlying",
  "Cnv_Ratio",
  "Delta",
  "Bond_Anchor",
  "Stock_Anchor",
  "underlying_current_price",
  "underlying_price_change",
  "current_price",
  "last_closing_price",
  "price_change",
];

const Converts_PNL = () => {
  // ===== UTILITY FUNCTIONS =====
  const checkAndClearStorageIfNewDay = () => {
    const lastVisitDate = localStorage.getItem("convertsLastVisitDate");
    const currentDate = new Date().toDateString();

    if (lastVisitDate !== currentDate) {
      // Clear all localStorage items
      localStorage.clear();

      // Set the new visit date
      localStorage.setItem("convertsLastVisitDate", currentDate);
    }
  };

  const GetData = async () => {
    try {
      const data_nukes = await fetchConvertNukes();
      setNukesData(data_nukes);
    } catch (error) {
      console.error("Error loading positions from JSON file:", error);
    }
  };

  // ===== STATE =====
  const theme = useTheme();
  const { datasets } = useContext(PositionsDataContext);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // Filter and sorting states
  const [convertsTradesGlobalFilter, setConvertsTradesGlobalFilter] = useState(
    () => localStorage.getItem("convertsTradesTableGlobalFilter") || "",
  );
  const [convertsTradesSorting, setConvertsTradesSorting] = useState(() => {
    const savedSorting = localStorage.getItem("convertsTradesTableSorting");
    return savedSorting
      ? JSON.parse(savedSorting)
      : [{ id: "StrategyName", desc: false }];
  });
  const [convertsTradesScrollPosition, setConvertsTradesScrollPosition] =
    useState(() => {
      const saved = localStorage.getItem("convertsTradesTableScrollPosition");
      return saved ? parseInt(saved, 10) : 0;
    });

  const [positionsFilter, setPositionsFilter] = useState(() => {
    checkAndClearStorageIfNewDay();
    return localStorage.getItem("convertsPnLFilter") || "";
  });
  const [positionsSorting, setPositionsSorting] = useState(() => {
    const savedSorting = localStorage.getItem("convertsPositionsTableSorting");
    return savedSorting
      ? JSON.parse(savedSorting)
      : [{ id: "StrategyName", desc: false }];
  });
  const [positionsScrollPosition, setPositionsScrollPosition] = useState(() => {
    return (
      parseInt(
        localStorage.getItem("convertsPositionsTableScrollPosition"),
        10,
      ) || 0
    );
  });
  const [positionsGlobalFilter, setPositionsGlobalFilter] = useState(
    () => localStorage.getItem("convertsPositionsTableGlobalFilter") || "",
  );

  // Data states
  const [allData, setAllData] = useState([]);
  const [nukes_data, setNukesData] = useState([]);
  const [maPositions, setMaPositions] = useState([]);
  const [tradedData, setTradedData] = useState([]);
  const [activeTab, setActiveTab] = useState("tab1");

  // Table states
  const [expandedPnLRows, setExpandedPnLRows] = useState(() => {
    const saved = localStorage.getItem("convertsPnLTableExpandedRows");
    return saved ? JSON.parse(saved) : {};
  });

  // Scroll position hook
  const { scrollPosition, setScrollPosition } =
    useScrollPosition("convertsPnLTable");

  // ===== EFFECTS =====
  useEffect(() => {
    if (datasets?.positions) setAllData(datasets.positions);
    if (datasets?.nukesData) setNukesData(datasets.nukesData);
  }, [datasets]);

  useEffect(() => {
    if (allData?.length > 0) {
      // Filter for Convertible Arbitrage positions instead of SPAC
      const maPositions = allData?.filter(
        (position) => position.StrategyType === "Convertible Arbitrage",
      );
      setMaPositions(maPositions || []);

      // Filter for positions with trades
      const positionsWithTrades = allData?.filter(
        (position) =>
          (position.buy_trade_quantity ||
            position.sell_trade_quantity ||
            position.short_trade_quantity ||
            position.cover_trade_quantity) &&
          position.StrategyType === "Convertible Arbitrage",
      );
      setTradedData(positionsWithTrades || []);
    }
  }, [allData]);

  useEffect(() => {
    checkAndClearStorageIfNewDay();
  }, []);

  // ===== CONFIG =====
  const pnlTableConfig = {
    columns: getConvertsPnlTableColumns(theme),
    data: maPositions,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    initialState: {
      grouping: ["StrategyName"],
      sorting: [{ id: "total_pnl", desc: true }],
      density: "compact",
      expanded: expandedPnLRows,
      columnPinning: isDesktop
        ? { left: ["StrategyName"] } // Pin StrategyName on desktop
        : {}, // No columns pinned in mobile mode
    },
    state: {
      expanded: expandedPnLRows,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function" ? updater(expandedPnLRows) : updater;
      setExpandedPnLRows(newExpanded);
      localStorage.setItem(
        "mabPnLTableExpandedRows",
        JSON.stringify(newExpanded),
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${row.depth * 2}rem`,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} /> // Responsive icon size
            ) : (
              <AddIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} />
            )}
            {row.getIsGrouped() && (
              <span
                style={{
                  marginLeft: "8px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }, // Responsive font size
                }}
              >
                {row.groupingValue}
              </span>
            )}
          </div>
        ),
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 130px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  };

  const nukesTableConfig = {
    columns: getNukesTableColumns(theme),
    data: nukes_data,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 130px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  };

  const tabs = [
    { value: "tab1", label: "Converts P&L" },
    { value: "tab2", label: "Converts Trades Today" },
    { value: "tab3", label: "Converts Positions" },
    { value: "tab4", label: "Converts Nukes" },
  ];

  // ===== MEMOIZED VALUES =====
  const pnl_table = useMaterialReactTable(pnlTableConfig);
  const nukes_table = useMaterialReactTable(nukesTableConfig);

  // ===== HANDLERS =====
  const handleTabClick = (tab) => {
    setActiveTab(tab);
  };

  // ===== RENDER LOGIC =====
  // (No conditional rendering logic in this component)

  // ===== RETURN =====
  return (
    <Box sx={{ width: "100%" }}>
      {/* Tab Navigation */}
      <CardWrapper sx={{ mb: 3 }}>
        <TabNavigation
          tabs={tabs}
          activeTab={activeTab}
          onTabChange={handleTabClick}
        />
      </CardWrapper>

      {/* Tab content */}
      <Box sx={{ backgroundColor: theme.palette.background.default }}>
        {activeTab === "tab1" && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 0,
              boxShadow: theme.shadows[1],
            }}
          >
            <MaterialReactTable table={pnl_table} />
          </Box>
        )}
        {activeTab === "tab2" && (
          <TradesTable
            data={tradedData}
            title="Converts Trades Today"
            globalFilter={convertsTradesGlobalFilter}
            setGlobalFilter={setConvertsTradesGlobalFilter}
            sorting={convertsTradesSorting}
            setSorting={setConvertsTradesSorting}
            scrollPosition={convertsTradesScrollPosition}
          />
        )}
        {activeTab === "tab3" && (
          <PositionsTable
            data={maPositions}
            title="Converts Positions"
            globalFilter={positionsGlobalFilter}
            setGlobalFilter={setPositionsGlobalFilter}
            sorting={positionsSorting}
            setSorting={setPositionsSorting}
            scrollPosition={positionsScrollPosition}
          />
        )}
        {activeTab === "tab4" && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 0,
              boxShadow: theme.shadows[1],
            }}
          >
            <PageHeader
              sx={{
                px: "20px",
                justifyContent: "flex-start",
                alignItems: "end",
              }}
              title={"Convert Nukes"}
              action={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center", // vertically center items
                    justifyContent: "flex-start", // left align
                    flexWrap: "wrap",
                    gap: 2,
                  }}
                >
                  <ExportToCSVButton
                    columns={columnsToExport}
                    theme={theme}
                    globalFilter=""
                    formatCurrency={formatCurrency}
                    formatNumber={formatNumber}
                    data={nukes_data}
                    filename={"Convert Nukes"}
                  />
                </Box>
              }
            />
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
                borderRadius: 0,
                boxShadow: theme.shadows[1],
              }}
            >
              <MaterialReactTable table={nukes_table} />
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default Converts_PNL;
